import { useRef, type CSSProperties, type MouseEvent } from "react";
import { motion } from "framer-motion";
import { services } from "@/mocks/serra";

const COLORS = ["#F89B37", "#45B6DA", "#F76B45", "#69B25A"];

function ServiceCard({
  icon,
  title,
  desc,
  color,
  index,
}: {
  icon: string;
  title: string;
  desc: string;
  color: string;
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  }

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      whileHover={{ y: -6 }}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-blue/10 p-8"
      style={
        {
          "--mx": "50%",
          "--my": "50%",
        } as CSSProperties
      }
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(320px circle at var(--mx) var(--my), ${color}22, transparent 70%)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: `inset 0 0 0 1px ${color}55` }}
      />

      <motion.span
        whileHover={{ rotateX: -12, rotateY: 12 }}
        style={{ transformStyle: "preserve-3d", perspective: 400 }}
        className="relative z-10 w-14 h-14 flex items-center justify-center rounded-xl text-2xl mb-6"
      >
        <span
          className="w-full h-full flex items-center justify-center rounded-xl"
          style={{ border: `1px solid ${color}66`, color, boxShadow: `0 0 24px -8px ${color}` }}
        >
          <i className={icon}></i>
        </span>
      </motion.span>

      <h3 className="relative z-10 text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="relative z-10 text-white/55 text-sm leading-relaxed">{desc}</p>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="servicos" className="py-24 lg:py-32 bg-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-cyan text-xs font-mono-tech tracking-[0.2em] uppercase mb-4 block">
            Nossos Serviços
          </span>
          <h2 className="text-white text-3xl lg:text-5xl font-bold mb-4">
            Tecnologia que impulsiona resultados
          </h2>
          <p className="text-white/55 text-base lg:text-lg">
            Soluções completas para transformar a presença digital da sua empresa.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              icon={service.icon}
              title={service.title}
              desc={service.desc}
              color={COLORS[index % COLORS.length]}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
